/**
 * Client-side WebP variants (matches legacy Sharp image-service dimensions / cover + centre).
 * Used instead of /process-image so only S3 keys are persisted by the API.
 */

const WEBP_QUALITY = 0.85;

async function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
    const url = URL.createObjectURL(blob);
    try {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = url;
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/** Cover resize: fills target box, centers crop (same idea as Sharp cover + centre). */
export function canvasResizeCover(image: HTMLImageElement, targetW: number, targetH: number): HTMLCanvasElement {
    const iw = image.naturalWidth || image.width;
    const ih = image.naturalHeight || image.height;
    const scale = Math.max(targetW / iw, targetH / ih);
    const sw = targetW / scale;
    const sh = targetH / scale;
    const sx = (iw - sw) / 2;
    const sy = (ih - sh) / 2;
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Canvas 2d context not available');
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, sx, sy, sw, sh, 0, 0, targetW, targetH);
    return canvas;
}

export async function canvasToWebpBlob(canvas: HTMLCanvasElement, quality = WEBP_QUALITY): Promise<Blob> {
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob(image/webp) failed'))),
            'image/webp',
            quality
        );
    });
}

export async function blobResizeCoverWebp(blob: Blob, w: number, h: number, quality = WEBP_QUALITY): Promise<Blob> {
    const img = await loadImageFromBlob(blob);
    const canvas = canvasResizeCover(img, w, h);
    return canvasToWebpBlob(canvas, quality);
}

/** Product main: lg 1600², sm 600², xs 200² (from cropped square JPEG blob). */
export async function buildProductMainWebpVariants(croppedJpegBlob: Blob): Promise<{ lg: Blob; sm: Blob; xs: Blob }> {
    const [lg, sm, xs] = await Promise.all([
        blobResizeCoverWebp(croppedJpegBlob, 1600, 1600),
        blobResizeCoverWebp(croppedJpegBlob, 600, 600),
        blobResizeCoverWebp(croppedJpegBlob, 200, 200),
    ]);
    return { lg, sm, xs };
}

/** Extra product gallery images: lg + sm only (matches saved model fields). */
export async function buildProductGalleryWebpVariants(croppedJpegBlob: Blob): Promise<{ lg: Blob; sm: Blob }> {
    const [lg, sm] = await Promise.all([
        blobResizeCoverWebp(croppedJpegBlob, 1600, 1600),
        blobResizeCoverWebp(croppedJpegBlob, 600, 600),
    ]);
    return { lg, sm };
}

/** Banner 4:1 step: wide crop is already 3200×800 — encode lg; sm = 1600×400 cover. */
export async function buildBanner41WebpVariants(wide3200x800Blob: Blob): Promise<{ lg: Blob; sm: Blob }> {
    const img = await loadImageFromBlob(wide3200x800Blob);
    const lgCanvas = canvasResizeCover(img, 3200, 800);
    const smCanvas = canvasResizeCover(img, 1600, 400);
    const [lg, sm] = await Promise.all([canvasToWebpBlob(lgCanvas), canvasToWebpBlob(smCanvas)]);
    return { lg, sm };
}

/** Banner 4:3 step → xs 800×600 cover. */
export async function buildBannerXsWebp(fourThreeCroppedBlob: Blob): Promise<Blob> {
    return blobResizeCoverWebp(fourThreeCroppedBlob, 800, 600);
}
